import { useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar as CalendarIcon, Clock, CheckCircle, ChevronRight, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { de } from "date-fns/locale";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const topics = [
  { value: "prozessoptimierung", label: "Prozessoptimierung" },
  { value: "digitalisierung", label: "Digitalisierung" },
  { value: "ki-automatisierung", label: "KI & Automatisierung" },
  { value: "software-entwicklung", label: "Software Entwicklung" },
  { value: "allgemein", label: "Allgemeine Beratung" },
];

const companySizes = [
  { value: "1-10", label: "1-10 Mitarbeiter" },
  { value: "11-50", label: "11-50 Mitarbeiter" },
  { value: "51-200", label: "51-200 Mitarbeiter" },
  { value: "201-500", label: "201-500 Mitarbeiter" },
  { value: "500+", label: "500+ Mitarbeiter" },
];

const industries = [
  { value: "it-software", label: "IT & Software" },
  { value: "finanz", label: "Finanzdienstleistungen" },
  { value: "industrie", label: "Industrie & Fertigung" },
  { value: "handel", label: "Handel & E-Commerce" },
  { value: "gesundheit", label: "Gesundheitswesen" },
  { value: "bildung", label: "Bildung" },
  { value: "beratung", label: "Beratung & Dienstleistung" },
  { value: "andere", label: "Andere" },
];

// Dynamic questions based on topic
const questionsByTopic = {
  "prozessoptimierung": [
    { id: "current_processes", label: "Welche Prozesse möchten Sie optimieren?", type: "textarea" },
    { id: "pain_points", label: "Was sind Ihre grössten Herausforderungen?", type: "textarea" },
    { id: "tools", label: "Welche Tools/Systeme nutzen Sie aktuell?", type: "text" },
  ],
  "digitalisierung": [
    { id: "digital_status", label: "Wie würden Sie Ihren aktuellen Digitalisierungsgrad beschreiben?", type: "select", options: ["Am Anfang", "Teilweise digitalisiert", "Fortgeschritten"] },
    { id: "goals", label: "Was sind Ihre Digitalisierungsziele?", type: "textarea" },
    { id: "systems", label: "Welche Systeme sollen integriert werden?", type: "text" },
  ],
  "ki-automatisierung": [
    { id: "ai_experience", label: "Haben Sie bereits Erfahrung mit KI-Lösungen?", type: "select", options: ["Keine Erfahrung", "Erste Tests", "Im Einsatz"] },
    { id: "use_cases", label: "Welche Anwendungsfälle interessieren Sie?", type: "textarea" },
    { id: "data_availability", label: "Welche Daten stehen zur Verfügung?", type: "text" },
  ],
  "software-entwicklung": [
    { id: "project_type", label: "Was für eine Software benötigen Sie?", type: "select", options: ["Web-Anwendung", "Mobile App", "Desktop Software", "API/Integration", "Anderes"] },
    { id: "requirements", label: "Beschreiben Sie kurz Ihre Anforderungen", type: "textarea" },
    { id: "timeline", label: "Gibt es einen gewünschten Zeitrahmen?", type: "text" },
  ],
  "allgemein": [
    { id: "goals", label: "Was erhoffen Sie sich vom Gespräch?", type: "textarea" },
  ],
};

export const SchedulerSection = () => {
  const ref = useRef(null);
  const formRef = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  // Form steps
  const [step, setStep] = useState(1); // 1: Date/Time, 2: Contact Info, 3: Company Info, 4: Questionnaire

  const goToStep = (s) => {
    setStep(s);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const [date, setDate] = useState(undefined);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState("");
  
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    position: "",
    linkedin: "",
    company: "",
    company_size: "",
    website: "",
    industry: "",
  });
  
  const [questionnaire, setQuestionnaire] = useState({});
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  useEffect(() => {
    checkCalendarStatus();
  }, []);

  useEffect(() => {
    if (date) {
      fetchAvailability(date);
    }
  }, [date]);

  const checkCalendarStatus = async () => {
    try {
      const response = await axios.get(`${API}/oauth/calendar/status`);
      setCalendarConnected(response.data.connected);
    } catch (error) {
      console.error("Error checking calendar status:", error);
    }
  };

  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const fetchAvailability = async (selectedDate) => {
    setLoadingSlots(true);
    try {
      const startDate = formatDateLocal(selectedDate);
      const endDate = startDate;
      
      const response = await axios.get(`${API}/calendar/availability`, {
        params: { start_date: startDate, end_date: endDate }
      });
      
      const slots = response.data.slots || [];
      setAvailableSlots(slots);
      setCalendarConnected(response.data.calendar_connected);
    } catch (error) {
      console.error("Error fetching availability:", error);
      setAvailableSlots(getDefaultSlots());
    } finally {
      setLoadingSlots(false);
    }
  };

  const getDefaultSlots = () => {
    const slots = [];
    for (let hour = 9; hour < 17; hour++) {
      slots.push({ time: `${hour.toString().padStart(2, '0')}:00`, available: true });
      slots.push({ time: `${hour.toString().padStart(2, '0')}:30`, available: true });
    }
    return slots;
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleQuestionnaireChange = (id, value) => {
    setQuestionnaire({ ...questionnaire, [id]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/calendar/book`, {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone || null,
        position: formData.position || null,
        linkedin: formData.linkedin || null,
        company: formData.company || null,
        company_size: formData.company_size || null,
        website: formData.website || null,
        industry: formData.industry || null,
        date: formatDateLocal(date),
        time_slot: selectedSlot,
        topic: selectedTopic,
        questionnaire: questionnaire,
      });

      setIsSuccess(true);
      
      if (response.data.calendar_event_created) {
        toast.success("Termin erfolgreich gebucht! Sie erhalten eine Bestätigung per E-Mail.");
      } else {
        toast.success("Terminanfrage erhalten! Wir melden uns bei Ihnen.");
      }
      
      setTimeout(() => {
        resetForm();
      }, 5000);
    } catch (error) {
      toast.error("Fehler beim Buchen. Bitte versuchen Sie es erneut.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setDate(undefined);
    setSelectedSlot(null);
    setSelectedTopic("");
    setFormData({
      first_name: "", last_name: "", email: "", phone: "", position: "", linkedin: "",
      company: "", company_size: "", website: "", industry: "",
    });
    setQuestionnaire({});
    setIsSuccess(false);
    setStep(1);
    setAvailableSlots([]);
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const disabledDays = (day) => {
    const dayDate = new Date(day);
    dayDate.setHours(0, 0, 0, 0);
    // 48h buffer — same as backend
    const earliest = new Date();
    earliest.setHours(earliest.getHours() + 48);
    earliest.setHours(0, 0, 0, 0);
    if (dayDate < earliest) return true;
    if (dayDate.getDay() === 0 || dayDate.getDay() === 6) return true;
    return false;
  };

  const getTimeSlotsForDate = () => {
    if (!date || availableSlots.length === 0) {
      return getDefaultSlots();
    }
    const dateStr = formatDateLocal(date);
    return availableSlots.filter(slot => slot.date === dateStr || !slot.date);
  };

  const timeSlots = getTimeSlotsForDate();
  const currentQuestions = questionsByTopic[selectedTopic] || [];

  const canProceedStep1 = date && selectedSlot && selectedTopic;
  const canProceedStep2 = formData.first_name && formData.last_name && formData.email;
  const canProceedStep3 = true; // Company info is optional
  const totalSteps = 4;

  return (
    <section
      id="scheduler"
      className="section-padding bg-white"
      data-testid="scheduler-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <span className="label-text">Terminvereinbarung</span>
          <h2 className="font-playfair text-3xl lg:text-4xl font-semibold text-lab-primary mt-4 max-w-2xl">
            Vereinbaren Sie Ihr kostenloses Erstgespräch
          </h2>
          <p className="text-lab-text-muted mt-4 max-w-xl">
            In einem 30-minütigen Gespräch analysieren wir Ihre aktuellen
            Herausforderungen und zeigen erste Lösungsansätze auf.
          </p>
        </motion.div>

        {/* Scheduler Grid */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left: Value Props */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-8"
          >
            <div className="bg-lab-bg-light p-8 border border-lab-border">
              <h3 className="font-playfair text-xl font-semibold text-lab-primary mb-6">
                Was Sie erwartet:
              </h3>
              <ul className="space-y-4">
                {[
                  "Analyse Ihrer aktuellen Prozesse und Herausforderungen",
                  "Identifikation von Optimierungspotentialen",
                  "Erste Handlungsempfehlungen und Roadmap",
                  "Unverbindliche Kostenschätzung",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-lab-accent flex-shrink-0 mt-0.5" />
                    <span className="text-lab-text">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex items-center gap-4 p-6 bg-lab-primary text-white">
              <Clock className="w-10 h-10 text-lab-accent" />
              <div>
                <p className="font-semibold">30 Minuten</p>
                <p className="text-sm text-gray-300">
                  Kostenlos & unverbindlich
                </p>
              </div>
            </div>
          </motion.div>

          {/* Right: Multi-Step Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            {isSuccess ? (
              <div className="bg-lab-bg-light border border-lab-accent p-12 text-center">
                <CheckCircle className="w-16 h-16 text-lab-accent mx-auto mb-6" />
                <h3 className="font-playfair text-2xl font-semibold text-lab-primary mb-3">
                  Termin gebucht!
                </h3>
                <p className="text-lab-text-muted">
                  Sie erhalten in Kürze eine Bestätigung per E-Mail mit allen Details und einer Kalendereinladung.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6 scroll-mt-24" data-testid="scheduler-form" ref={formRef}>
                {/* Progress Bar */}
                <div className="flex items-center gap-2 mb-8">
                  {[1, 2, 3, 4].map((s) => (
                    <div key={s} className="flex-1 flex items-center">
                      <div className={`flex-1 h-1 ${s <= step ? 'bg-lab-accent' : 'bg-lab-border'}`} />
                    </div>
                  ))}
                </div>
                <p className="text-sm text-lab-text-muted mb-4">
                  Schritt {step} von {totalSteps}: {
                    step === 1 ? "Termin wählen" :
                    step === 2 ? "Kontaktdaten" :
                    step === 3 ? "Unternehmen" :
                    "Vorbereitung"
                  }
                </p>

                {/* Step 1: Date/Time/Topic */}
                {step === 1 && (
                  <div className="space-y-6">
                    {/* Calendar */}
                    <div className="bg-lab-bg-light p-6 border border-lab-border">
                      <div className="flex items-center gap-2 mb-4">
                        <CalendarIcon className="w-5 h-5 text-lab-accent" />
                        <Label className="font-semibold text-lab-primary">Datum auswählen</Label>
                      </div>
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={disabledDays}
                        locale={de}
                        className="rounded-none border-0"
                      />
                    </div>

                    {/* Time Slots */}
                    {date && (
                      <div className="bg-lab-bg-light p-6 border border-lab-border">
                        <div className="flex items-center gap-2 mb-4">
                          <Clock className="w-5 h-5 text-lab-accent" />
                          <Label className="font-semibold text-lab-primary">Uhrzeit auswählen</Label>
                          {loadingSlots && <span className="text-xs text-lab-text-muted ml-2">Lade...</span>}
                        </div>
                        <div className="grid grid-cols-4 gap-2">
                          {timeSlots.map((slot) => (
                            <button
                              key={slot.time}
                              type="button"
                              onClick={() => slot.available && setSelectedSlot(slot.time)}
                              disabled={!slot.available}
                              className={`py-2 px-3 text-sm border transition-colors duration-200 ${
                                !slot.available
                                  ? "bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed line-through"
                                  : selectedSlot === slot.time
                                  ? "bg-lab-accent text-lab-primary border-lab-accent"
                                  : "bg-white border-lab-border text-lab-text hover:border-lab-accent"
                              }`}
                            >
                              {slot.time}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Topic */}
                    <div className="space-y-2">
                      <Label className="font-semibold text-lab-primary">Beratungsthema *</Label>
                      <Select value={selectedTopic} onValueChange={setSelectedTopic}>
                        <SelectTrigger className="rounded-none border-lab-border">
                          <SelectValue placeholder="Wählen Sie ein Thema" />
                        </SelectTrigger>
                        <SelectContent>
                          {topics.map((topic) => (
                            <SelectItem key={topic.value} value={topic.value}>
                              {topic.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <button
                      type="button"
                      onClick={() => goToStep(2)}
                      disabled={!canProceedStep1}
                      className="btn-primary rounded-none w-full flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      Weiter <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* Step 2: Contact Info */}
                {step === 2 && (
                  <div className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="font-semibold text-lab-primary">Vorname *</Label>
                        <Input
                          name="first_name"
                          value={formData.first_name}
                          onChange={handleInputChange}
                          className="rounded-none border-lab-border"
                          placeholder="Max"
                          required
                          data-testid="scheduler-first-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="font-semibold text-lab-primary">Nachname *</Label>
                        <Input
                          name="last_name"
                          value={formData.last_name}
                          onChange={handleInputChange}
                          className="rounded-none border-lab-border"
                          placeholder="Mustermann"
                          required
                          data-testid="scheduler-last-name"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="font-semibold text-lab-primary">E-Mail *</Label>
                        <Input
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="rounded-none border-lab-border"
                          placeholder="max@beispiel.ch"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="font-semibold text-lab-primary">Telefon</Label>
                        <Input
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="rounded-none border-lab-border"
                          placeholder="+41 79 123 45 67"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="font-semibold text-lab-primary">Position</Label>
                        <Input
                          name="position"
                          value={formData.position}
                          onChange={handleInputChange}
                          className="rounded-none border-lab-border"
                          placeholder="CEO, CTO, etc."
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="font-semibold text-lab-primary">LinkedIn Profil</Label>
                      <Input
                        name="linkedin"
                        value={formData.linkedin}
                        onChange={handleInputChange}
                        className="rounded-none border-lab-border"
                        placeholder="https://linkedin.com/in/..."
                      />
                    </div>

                    <div className="flex gap-4">
                      <button
                        type="button"
                        onClick={() => goToStep(1)}
                        className="btn-secondary rounded-none flex-1 flex items-center justify-center gap-2"
                      >
                        <ChevronLeft className="w-4 h-4" /> Zurück
                      </button>
                      <button
                        type="button"
                        onClick={() => goToStep(3)}
                        disabled={!canProceedStep2}
                        className="btn-primary rounded-none flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        Weiter <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 3: Company Info */}
                {step === 3 && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="font-semibold text-lab-primary">Unternehmen</Label>
                      <Input
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="rounded-none border-lab-border"
                        placeholder="Firma AG"
                      />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="font-semibold text-lab-primary">Unternehmensgrösse</Label>
                        <Select value={formData.company_size} onValueChange={(v) => setFormData({...formData, company_size: v})}>
                          <SelectTrigger className="rounded-none border-lab-border">
                            <SelectValue placeholder="Anzahl Mitarbeiter" />
                          </SelectTrigger>
                          <SelectContent>
                            {companySizes.map((size) => (
                              <SelectItem key={size.value} value={size.value}>
                                {size.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="font-semibold text-lab-primary">Branche</Label>
                        <Select value={formData.industry} onValueChange={(v) => setFormData({...formData, industry: v})}>
                          <SelectTrigger className="rounded-none border-lab-border">
                            <SelectValue placeholder="Branche wählen" />
                          </SelectTrigger>
                          <SelectContent>
                            {industries.map((ind) => (
                              <SelectItem key={ind.value} value={ind.value}>
                                {ind.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="font-semibold text-lab-primary">Website</Label>
                      <Input
                        name="website"
                        value={formData.website}
                        onChange={handleInputChange}
                        className="rounded-none border-lab-border"
                        placeholder="https://www.beispiel.ch"
                      />
                    </div>

                    <div className="flex gap-4">
                      <button
                        type="button"
                        onClick={() => goToStep(2)}
                        className="btn-secondary rounded-none flex-1 flex items-center justify-center gap-2"
                      >
                        <ChevronLeft className="w-4 h-4" /> Zurück
                      </button>
                      <button
                        type="button"
                        onClick={() => goToStep(4)}
                        className="btn-primary rounded-none flex-1 flex items-center justify-center gap-2"
                      >
                        Weiter <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 4: Questionnaire */}
                {step === 4 && (
                  <div className="space-y-4">
                    <p className="text-sm text-lab-text-muted mb-4">
                      Diese Informationen helfen uns, uns optimal auf das Gespräch vorzubereiten.
                    </p>
                    
                    {currentQuestions.map((q) => (
                      <div key={q.id} className="space-y-2">
                        <Label className="font-semibold text-lab-primary">{q.label}</Label>
                        {q.type === "textarea" ? (
                          <Textarea
                            value={questionnaire[q.id] || ""}
                            onChange={(e) => handleQuestionnaireChange(q.id, e.target.value)}
                            className="rounded-none border-lab-border min-h-[80px]"
                            placeholder="Ihre Antwort..."
                          />
                        ) : q.type === "select" ? (
                          <Select 
                            value={questionnaire[q.id] || ""} 
                            onValueChange={(v) => handleQuestionnaireChange(q.id, v)}
                          >
                            <SelectTrigger className="rounded-none border-lab-border">
                              <SelectValue placeholder="Bitte wählen" />
                            </SelectTrigger>
                            <SelectContent>
                              {q.options.map((opt) => (
                                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input
                            value={questionnaire[q.id] || ""}
                            onChange={(e) => handleQuestionnaireChange(q.id, e.target.value)}
                            className="rounded-none border-lab-border"
                            placeholder="Ihre Antwort..."
                          />
                        )}
                      </div>
                    ))}

                    <div className="flex gap-4 pt-4">
                      <button
                        type="button"
                        onClick={() => goToStep(3)}
                        className="btn-secondary rounded-none flex-1 flex items-center justify-center gap-2"
                      >
                        <ChevronLeft className="w-4 h-4" /> Zurück
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="btn-primary rounded-none flex-1 disabled:opacity-50"
                      >
                        {isSubmitting ? "Wird gebucht..." : "Termin buchen"}
                      </button>
                    </div>
                    
                    <p className="text-xs text-lab-text-muted text-center pt-2">
                      Nach der Buchung erhalten Sie eine Bestätigung per E-Mail.
                    </p>
                  </div>
                )}
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default SchedulerSection;
